import { useEffect, useMemo } from 'react';
import { Card, Text, makeStyles, tokens } from '@fluentui/react-components';
import {
  Tag24Regular,
  ShieldLock24Regular,
  ShieldCheckmark24Regular,
  CheckmarkCircle24Regular,
} from '@fluentui/react-icons';
import { useAppDispatch, useAppSelector } from '../hooks';
import { fetchTenantCounts } from '../slices/tenantCountsSlice';
import { makeSelectTenantKPIBlocks } from '../selectors/tenantkpiSelectors';
import { TenantKPICardsStyles } from './Styles';

const pct = (num: number, den: number) => (den ? Math.round((num / den) * 100) : 0);

// Keyboard event handler for card interaction
const handleCardKeyDown = (event: React.KeyboardEvent, cardType: string) => {
  if (event.key === 'Enter' || event.key === ' ') {
    event.preventDefault();
    // You can add card-specific actions here if needed
    console.log(`${cardType} card activated`);
  }
};

export default function TenantKPICards({ tenantId }: { tenantId: string }) {
  const s = TenantKPICardsStyles();
  const dispatch = useAppDispatch();

  // Fetch tenant counts when tenantId changes
  useEffect(() => {
    if (tenantId) {
      dispatch(fetchTenantCounts(tenantId));
    }
  }, [ tenantId]);

  // Stable, per-tenant selector
  const selectTenant = useMemo(() => makeSelectTenantKPIBlocks(tenantId), [tenantId]);
  // selectTenant returns a predictable KPI shape; assert type for TS
  const kpi = useAppSelector(selectTenant) as {
    sits: { available: number; total: number };
    labels: { available: number; total: number };
    policies: { available: number; total: number };
    overall: number;
  };

  return (
    <div className={s.cardGrid} role="region" aria-label="Tenant KPI metrics">
      <Card
        className={s.card}
        tabIndex={0}
        role="article"
        aria-label={`Deployed SITs: ${kpi.sits.available} out of ${kpi.sits.total} total, ${pct(kpi.sits.available, kpi.sits.total)}% deployed`}
        onKeyDown={(e) => handleCardKeyDown(e, 'SITs')}
      >
        <div className={s.headerRow}>
          <Text className={s.title}>Deployed SITs</Text>
          <Tag24Regular className={s.icon} aria-hidden="true" />
        </div>
        <Text className={s.valueText}>{kpi.sits.available}</Text>
        <Text className={s.subText}>out of {kpi.sits.total} total</Text>
        <div className={s.progressWrap}>
          <div className={s.progressTrack} />
          <div
            className={s.progressFill}
            style={{ width: `${pct(kpi.sits.available, kpi.sits.total)}%` }}
            role="progressbar"
            aria-valuenow={pct(kpi.sits.available, kpi.sits.total)}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label={`SITs progress: ${pct(kpi.sits.available, kpi.sits.total)}%`}
          />
        </div>
      </Card>

      <Card
        className={s.card}
        tabIndex={0}
        role="article"
        aria-label={`Deployed Labels: ${kpi.labels.available} out of ${kpi.labels.total} total, ${pct(kpi.labels.available, kpi.labels.total)}% deployed`}
        onKeyDown={(e) => handleCardKeyDown(e, 'Labels')}
      >
        <div className={s.headerRow}>
          <Text className={s.title}>Deployed Labels</Text>
          <ShieldLock24Regular className={s.icon} aria-hidden="true" />
        </div>
        <Text className={s.valueText}>{kpi.labels.available}</Text>
        <Text className={s.subText}>out of {kpi.labels.total} total</Text>
        <div className={s.progressWrap}>
          <div className={s.progressTrack} />
          <div
            className={s.progressFill}
            style={{ width: `${pct(kpi.labels.available, kpi.labels.total)}%` }}
            role="progressbar"
            aria-valuenow={pct(kpi.labels.available, kpi.labels.total)}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label={`Labels progress: ${pct(kpi.labels.available, kpi.labels.total)}%`}
          />
        </div>
      </Card>

      <Card
        className={s.card}
        tabIndex={0}
        role="article"
        aria-label={`Deployed Policies: ${kpi.policies.available} out of ${kpi.policies.total} total, ${pct(kpi.policies.available, kpi.policies.total)}% deployed`}
        onKeyDown={(e) => handleCardKeyDown(e, 'Policies')}
      >
        <div className={s.headerRow}>
          <Text className={s.title}>Deployed Policies</Text>
          <ShieldCheckmark24Regular className={s.icon} aria-hidden="true" />
        </div>
        <Text className={s.valueText}>{kpi.policies.available}</Text>
        <Text className={s.subText}>out of {kpi.policies.total} total</Text>
        <div className={s.progressWrap}>
          <div className={s.progressTrack} />
          <div
            className={s.progressFill}
            style={{ width: `${pct(kpi.policies.available, kpi.policies.total)}%` }}
            role="progressbar"
            aria-valuenow={pct(kpi.policies.available, kpi.policies.total)}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label={`Policies progress: ${pct(kpi.policies.available, kpi.policies.total)}%`}
          />
        </div>
      </Card>

      <Card
        className={s.card}
        tabIndex={0}
        role="article"
        aria-label={`Overall Deployment: ${kpi.overall}% across all items`}
        onKeyDown={(e) => handleCardKeyDown(e, 'Overall Coverage')}
      >
        <div className={s.headerRow}>
          <Text className={s.title}>Overall Deployment</Text>
          <CheckmarkCircle24Regular className={s.icon} aria-hidden="true" />
        </div>
        <Text className={s.valueText}>{kpi.overall}%</Text>
        <Text className={s.subText}>Deployment percentage</Text>
        <div className={s.progressWrap}>
          <div className={s.progressTrack} />
          <div
            className={s.progressFill}
            style={{ width: `${kpi.overall}%` }}
            role="progressbar"
            aria-valuenow={kpi.overall}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label={`Overall deployment progress: ${kpi.overall}%`}
          />
        </div>
      </Card>
    </div>
  );
}
